import { ButtonContent } from '../../ui/ButtonContent';
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSupabase } from '../../../lib/SupabaseProvider';
import { captureException } from '../../../lib/telemetry/posthog/frontend';
import { Select } from '../../ui/Select';

/** Request type options for the support form. */
const REQUEST_TYPES = [
  { value: 'bug_report', label: 'Bug Report' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'account_issue', label: 'Account Issue' },
  { value: 'billing_question', label: 'Billing Question' },
  { value: 'general_inquiry', label: 'General Inquiry' },
];

/**
 * Help & Support section of the Settings page.
 * Sends support requests via the /api/support endpoint.
 * Reads `?report=` from URL search params to pre-select a request type.
 */
export const SupportSection: React.FC = () => {
  const { session } = useSupabase();
  const [searchParams] = useSearchParams();

  const [requestType, setRequestType] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  /** Pre-select request type and message from URL search params. */
  useEffect(() => {
    const reportParam = searchParams.get('report');
    if (reportParam && REQUEST_TYPES.some((t) => t.value === reportParam)) {
      setRequestType(reportParam);
    }
    const messageParam = searchParams.get('message');
    if (messageParam) {
      setMessage(messageParam);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!requestType || !message.trim() || !session?.access_token) return;

    setSubmitting(true);
    setStatus(null);

    try {
      const res = await fetch('/api/support', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          requestType,
          message: message.trim(),
        }),
      });

      const data = await res.json() as { success?: boolean; error?: string };

      if (res.ok && data.success) {
        setStatus({ type: 'success', text: 'Your message has been sent. We\'ll get back to you soon.' });
        setRequestType('');
        setMessage('');
      } else {
        setStatus({ type: 'error', text: data.error || 'Failed to send message. Please try again.' });
      }
    } catch (error) {
      captureException(
        error instanceof Error ? error : new Error(String(error)),
        'SupportSection.handleSubmit',
        { requestType }
      );
      setStatus({ type: 'error', text: 'Failed to send message. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section>
      <h2 className="text-lg font-semibold text-foreground mb-2">Help & Support</h2>
      <p className="text-sm text-muted-foreground mb-6">
        Have a question or found a bug? Send us a message and we'll get back to you.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          value={requestType}
          onChange={setRequestType}
          options={REQUEST_TYPES}
          placeholder="Select a request type..."
          label="Request Type"
        />

        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-medium block mb-2">
            Message
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Describe your issue or question..."
            rows={5}
            maxLength={5000}
            required
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm placeholder-subtle-foreground focus:outline-none focus:border-volt resize-none"
          />
          <p className="text-xs text-subtle-foreground mt-1 text-right">{message.length}/5000</p>
        </div>

        {status && (
          <p className={`text-sm ${status.type === 'success' ? 'text-success' : 'text-danger'}`}>
            {status.text}
          </p>
        )}

        <button aria-busy={submitting}
          type="submit"
          disabled={submitting || !requestType || !message.trim()}
          className="px-6 py-2 button-accent font-bold text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ButtonContent loading={submitting}>Send Message</ButtonContent>
        </button>
      </form>

      {/* Legal links - relocated here from the sidebar dropdown to keep the
          sidebar focused on frequent actions. */}
      <div className="mt-10 pt-6 border-t border-border">
        <h3 className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-3">Legal</h3>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <a
            href="https://holito.app/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            Privacy Policy
          </a>
          <a
            href="https://holito.app/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            Terms of Service
          </a>
        </div>
      </div>
    </section>
  );
};
